import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";

import { Box, IconButton, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { CustomizedBox } from "../../../Custom/CustomizedBox";
import { CustomizedTooltip } from "../../../Custom/CustomizedTooltip";
import BillingAddressModal from "../../../UI/Modal/BillingAddressModal";
import PurchaseDeliveryAddressModal from "../../../UI/Modal/PurchaseDeliveryAddressModal";

import { useModal } from "../../../../hooks/use-modal";
import { useDisable } from "../../../../hooks/use-disable";
import { countryFormatter } from "../../../../utils/Formatter/Global";
import SubHeader from "../SubHeader";

type Props = {
  forceDisabled?: boolean;
  documentType?: string;
};

const AddressInfo = ({ forceDisabled, documentType }: Props) => {
  const { t } = useTranslation();
  const [disabled] = useDisable();

  const { control, getValues, watch } = useFormContext();

  const vendorUniqueId = watch("vendor_unique_id");

  const {
    modal: openBillingAddress,
    openModalHandler: openBillingAddressHandler,
    closeModalHandler: closeBillingAddress,
  } = useModal();

  const {
    modal: openDeliveryAddress,
    openModalHandler: openDeliveryAddressHandler,
    closeModalHandler: closeDeliveryAddress,
  } = useModal();

  const renderAddressString = (values: string) => {
    const addressValues = getValues(values);
    if (addressValues) {
      const {
        address_name,
        sub_district,
        district,
        province,
        postal_code,
        country,
      } = addressValues;

      const orderedValues = {
        address_name,
        sub_district,
        district,
        province,
        postal_code,
        country,
      };
      let result = "";

      if (addressValues && orderedValues) {
        if (Array.isArray(addressValues)) {
          addressValues.forEach((item) => {
            if (item?.length > 0) {
              result = result + item + ", ";
            }
          });
        } else {
          const keys = Object.keys(orderedValues);
          keys.forEach((key) => {
            let newValue = "";
            if (key === "country") {
              newValue =
                countryFormatter(
                  orderedValues[key as keyof typeof orderedValues]
                ) || "";
            } else {
              newValue = orderedValues[key as keyof typeof orderedValues];
            }

            if (newValue && newValue.length > 0) {
              result = result + newValue + ", ";
            }
          });
        }
        if (result?.trim().length === 0) {
          return "-";
        }
      }
      // remove whitespace and last comma
      return result?.trim().slice(0, -1);
      // remove whitespace and last comma
    } else return "-";
  };

  return (
    <Fragment>
      <CustomizedBox margin={0}>
        <Box display="flex" gap={1} alignItems="center">
          <SubHeader title={t("address.billing_address")} />
          {!disabled && vendorUniqueId ? (
            <CustomizedTooltip title={`แก้ไข${t("address.billing_address")}`}>
              <IconButton onClick={openBillingAddressHandler} color="primary">
                <EditIcon />
              </IconButton>
            </CustomizedTooltip>
          ) : null}
        </Box>
        <Typography my={2}>
          {renderAddressString("vendor_details.billing_address")}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <SubHeader title={t("address.delivery_address")} />
          {!disabled ? (
            <CustomizedTooltip title={`แก้ไข${t("address.delivery_address")}`}>
              <IconButton onClick={openDeliveryAddressHandler} color="primary">
                <EditIcon />
              </IconButton>
            </CustomizedTooltip>
          ) : null}
        </Box>

        <Typography mt={2}>
          {renderAddressString("delivery_address")}
        </Typography>
      </CustomizedBox>
      <BillingAddressModal
        name="vendor_details"
        open={openBillingAddress}
        handleClose={closeBillingAddress}
        viewMode={false}
      />
      <PurchaseDeliveryAddressModal
        name="vendor_details"
        open={openDeliveryAddress}
        handleClose={closeDeliveryAddress}
        viewMode={false}
      />
    </Fragment>
  );
};

export default AddressInfo;
